import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { getSpendLensCategories } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { IAbstractLensItem } from '@/types'
import { SpendLensCategoryModel } from '@/types/clarityLens'
import { areFilterParamsValid } from '@/utils/filterParams'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useSpendLensCategories(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () => areFilterParamsValid(startDate, endDate, branches),
    [startDate, endDate, branches]
  )
  const loading = !isReady || isFetching

  const [categoriesData, setCategoriesData] =
    useState<SpendLensCategoryModel[]>()
  const [trendCategoriesData, setTrendCategoriesData] =
    useState<SpendLensCategoryModel[]>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { data } = await fetchCategoriesData(branches, startDate, endDate)
        setCategoriesData(data)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [branches, startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange
        const { data } = await fetchCategoriesData(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )
        setTrendCategoriesData(data)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [branches, startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [fetchData, fetchTrendData, isReady])

  const refetch = useCallback(() => {
    console.log('Refetching spend lens categories...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  // Format categories data for display with trend data
  const formattedCategoriesData = useMemo(() => {
    if (!categoriesData || !trendCategoriesData) return []

    // Create a map of trend data by category name for easy lookup
    const trendDataMap = new Map(
      trendCategoriesData.map((category) => [category.category, category])
    )

    return categoriesData.map(
      (category: SpendLensCategoryModel): IAbstractLensItem<any> => {
        const trendCategory = trendDataMap.get(category.category)

        return {
          item: category.category,
          current: {
            totalSpend: category.totalSpend,
            spendSharePercentage: category.spendSharePercentage,
            averageUnitPrice: category.averageUnitPrice,
            activeSkuCount: category.activeSkuCount,
          },
          prev: trendCategory
            ? {
                totalSpend: trendCategory.totalSpend,
                spendSharePercentage: trendCategory.spendSharePercentage,
                averageUnitPrice: trendCategory.averageUnitPrice,
                activeSkuCount: trendCategory.activeSkuCount,
              }
            : undefined,
        }
      }
    )
  }, [categoriesData, trendCategoriesData])

  return {
    formattedCategoriesData,
    loading,
    refetch,
  }
}

async function fetchCategoriesData(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  data: SpendLensCategoryModel[]
}> {
  try {
    const response = await getSpendLensCategories(branches, startDate, endDate)

    const data: SpendLensCategoryModel[] = response.map((responseItem) => {
      return convertData(
        responseItem.category,
        responseItem.total_spend,
        responseItem.spend_share_percentage,
        responseItem.average_unit_price,
        responseItem.active_sku_count
      )
    })

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  category: string,
  totalSpend: number,
  spendSharePercentage: number,
  averageUnitPrice: number,
  activeSkuCount: number
): SpendLensCategoryModel {
  return {
    category,
    totalSpend: formatMetric(totalSpend, 2),
    spendSharePercentage: formatMetric(spendSharePercentage, 2),
    averageUnitPrice: formatMetric(averageUnitPrice, 2),
    activeSkuCount: formatMetric(activeSkuCount, 2),
  }
}
